import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';
import cn from 'classnames';

// date-fns functions
import isValid from 'date-fns/isValid';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import endOfDay from 'date-fns/endOfDay';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import getDay from 'date-fns/getDay';
import getDate from 'date-fns/getDate';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import getDaysInMonth from 'date-fns/getDaysInMonth';

import { IconButton } from 'components';

import styles from './DateSelector.module.scss';

export type DateRange = [Date, Date] | [null, null];

export interface DateSelectorProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** current date selected */
  value: Date | null;
  /** on date change handler */
  onDateChange: (date: Date) => void;
  /** trigger the change on the selected date */
  triggerChangeOnSelectedDate?: boolean;
  /** select current date when value is null */
  selectCurrentDayWhenNoValue?: boolean;
  /** minimum date */
  minimumDate?: Date;
  /** maximum date */
  maximumDate?: Date;
  /** select minimum date as a start of range */
  preselectMinDate?: boolean;
  /** Start date of range selection? */
  rangeStartDate?: Date | null;
}

const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const DateSelector: React.FC<DateSelectorProps> = memo(
  ({
    className = '',
    style,
    value,
    onDateChange,
    minimumDate,
    maximumDate,
    triggerChangeOnSelectedDate = false,
    selectCurrentDayWhenNoValue = false,
    preselectMinDate,
    rangeStartDate,
  }) => {
    const contentClassNames = cn([styles.container], className);

    const selected = useMemo(() => {
      return value && isValid(value) ? new Date(value) : new Date();
    }, [value]);

    const [dateContext, setDateContext] = useState(selected);

    const areParametersValid = useMemo(() => {
      let momentDate, momentMinDate, momentMaxDate;
      const [d1, d2, d3] = [
        new Date(selected),
        minimumDate ? new Date(minimumDate) : new Date(),
        maximumDate ? new Date(maximumDate) : new Date(),
      ];
      if (isValid(d1) && isValid(d2) && isValid(d3)) {
        momentDate = selected ? d1 : null;
        momentMinDate = minimumDate ? startOfDay(d2) : null;
        momentMaxDate = maximumDate ? endOfDay(d3) : null;
      } else {
        console.error('Please pass dates in required format');
        return false;
      }

      if (
        momentMinDate &&
        momentMaxDate &&
        isAfter(momentMinDate, momentMaxDate)
      ) {
        console.error('The minimum date should not be after maximum date');
        return false;
      }

      if (!momentDate) {
        console.error('Please pass value prop, it is mandatory');
        return false;
      }

      return true;
    }, [selected, minimumDate, maximumDate]);

    const [selectedMonth, selectedYear, daysInMonth, firstDayOfMonth] =
      useMemo(() => {
        return [
          getMonth(dateContext),
          getYear(dateContext),
          getDaysInMonth(dateContext),
          Number(getDay(startOfMonth(dateContext))),
        ];
      }, [dateContext]);

    useEffect(() => {
      const [month, year] = [getMonth(selected), getYear(selected)];
      if (selectedMonth !== month || selectedYear !== year) {
        setDateContext(new Date(year, month));
      }
      // eslint-disable-next-line
    }, [selected]);

    const [selectedDateValue, selectedMonthValue, selectedYearValue] =
      useMemo(() => {
        const date = new Date(selected);
        return [getDate(date), getMonth(date), getYear(date)];
      }, [selected]);

    const [minDate, minMonth, minYear] = useMemo(() => {
      if (minimumDate) {
        const date = new Date(minimumDate);
        return [getDate(date), getMonth(date), getYear(date)];
      } else {
        return [null, null, null];
      }
    }, [minimumDate]);

    const [maxDate, maxMonth, maxYear] = useMemo(() => {
      if (maximumDate) {
        const date = new Date(maximumDate);
        return [getDate(date), getMonth(date), getYear(date)];
      } else {
        return [null, null, null];
      }
    }, [maximumDate]);

    const weekdaysHeader = useMemo(() => {
      return (
        <tr>
          {weekdays.map((weekday, index) => (
            <th key={index} role="columnheader">
              <span className={styles.weekday}>{weekday}</span>
            </th>
          ))}
        </tr>
      );
    }, []);

    const emptySlots = useMemo(() => {
      return Array.from({ length: firstDayOfMonth }).map((blank, index) => (
        <span key={index}></span>
      ));
    }, [firstDayOfMonth]);

    const onChange = useCallback(
      (event) => {
        if (
          event.target.nodeName &&
          event.target.nodeName === 'BUTTON' &&
          event.target.textContent
        ) {
          event.stopPropagation();
          event.preventDefault();
          const { textContent } = event.target;
          const date = Number(textContent);
          const selectedMomentDate = new Date(
            selectedYear,
            selectedMonth,
            date
          );
          if (
            !(
              (minimumDate &&
                isBefore(selectedMomentDate, startOfDay(minimumDate))) ||
              (maximumDate &&
                isAfter(selectedMomentDate, endOfDay(maximumDate)))
            )
          ) {
            onDateChange(selectedMomentDate);
          }
        }
      },
      [selectedMonth, selectedYear, onDateChange, minimumDate, maximumDate]
    );

    const [
      invalidMonth,
      previousMonthDisabled,
      nextMonthDisabled,
      previousYearDisabled,
      nextYearDisabled,
    ]: [boolean, boolean, boolean, boolean, boolean] = useMemo(() => {
      if (!(minYear || maxYear)) return [false, false, false, false, false];

      const invalidMonth =
        (minYear && selectedYear < minYear) ||
        (maxYear && selectedYear > maxYear) ||
        (minYear &&
          selectedYear === minYear &&
          minMonth !== null &&
          selectedMonth < minMonth) ||
        (maxMonth !== null &&
          selectedMonth > maxMonth &&
          maxYear &&
          selectedYear >= maxYear);

      const previousMonthDisabled =
        (minYear && selectedYear < minYear) ||
        (minYear &&
          minYear === selectedYear &&
          minMonth !== null &&
          selectedMonth <= minMonth);
      const nextMonthDisabled =
        (maxYear && selectedYear > maxYear) ||
        (maxYear &&
          maxYear === selectedYear &&
          maxMonth !== null &&
          selectedMonth >= maxMonth);

      const previousYearDisabled =
        (minYear && selectedYear <= minYear) ||
        (minYear &&
          minMonth &&
          selectedMonth < minMonth &&
          selectedYear === minYear + 1);
      const nextYearDisabled =
        (maxYear && selectedYear >= maxYear) ||
        (maxYear &&
          maxMonth &&
          selectedMonth > maxMonth &&
          selectedYear === maxYear - 1);

      return [
        Boolean(invalidMonth),
        Boolean(previousMonthDisabled),
        Boolean(nextMonthDisabled),
        Boolean(previousYearDisabled),
        Boolean(nextYearDisabled),
      ];
    }, [selectedYear, selectedMonth, minMonth, minYear, maxMonth, maxYear]);

    const days = useMemo(() => {
      return Array.from({ length: daysInMonth }).map((_, index) => {
        const date = index + 1;
        const dateDisabled =
          invalidMonth ||
          (minYear &&
            selectedYear === minYear &&
            minMonth !== null &&
            selectedMonth === minMonth &&
            minDate &&
            date < minDate) ||
          (maxDate &&
            date > maxDate &&
            maxMonth !== null &&
            selectedMonth === maxMonth &&
            maxYear &&
            selectedYear === maxYear);

        const currentDate = new Date(
          selectedYear,
          selectedMonth,
          date,
          0,
          0,
          0,
          0
        );
        const selectedDate = new Date(
          selectedYearValue,
          selectedMonthValue,
          selectedDateValue,
          0,
          0,
          0,
          0
        );
        const selected = +currentDate === +selectedDate;

        const selectedStartDate = rangeStartDate
          ? +startOfDay(new Date(rangeStartDate)) === +currentDate
          : false;

        const inBetween = (function () {
          let startDate;
          if (preselectMinDate) {
            startDate = minimumDate;
          } else if (rangeStartDate) {
            startDate = rangeStartDate;
          }

          return (
            value &&
            startDate &&
            isAfter(currentDate, startDate) &&
            isBefore(currentDate, selectedDate)
          );
        })();

        const dateClassNames = cn({
          [styles.date]: true,
          [styles.selected]: selected && (selectCurrentDayWhenNoValue || value),
          [styles['range-end-date']]:
            (preselectMinDate && minimumDate) || (selected && rangeStartDate),
          [styles['range-start-date']]: selectedStartDate,
          [styles.disabled]:
            selected &&
            !triggerChangeOnSelectedDate &&
            (selectCurrentDayWhenNoValue || value),
          [styles.preselect]:
            preselectMinDate &&
            date === minDate &&
            selectedMonth === minMonth &&
            selectedYear === minYear,
          [styles['no-value-selected']]: !value,
          [styles.inbetween]: inBetween,
        });

        return (
          <button
            disabled={Boolean(dateDisabled)}
            key={index}
            type="button"
            className={dateClassNames}
            aria-label={`${months[selectedMonth]} ${date}, ${selectedYear}`}
          >
            {date}
          </button>
        );
      });
    }, [
      value,
      daysInMonth,
      invalidMonth,
      selectedDateValue,
      selectedMonthValue,
      selectedYearValue,
      selectedMonth,
      selectedYear,
      minYear,
      maxYear,
      minMonth,
      minDate,
      maxMonth,
      maxDate,
      selectCurrentDayWhenNoValue,
      triggerChangeOnSelectedDate,
      preselectMinDate,
      minimumDate,
      rangeStartDate,
    ]);

    const monthDays = useMemo(() => {
      const dayCells = [...emptySlots, ...days];
      const count = dayCells.length;
      let cellIndex = 0;
      const rows = [];
      for (let i = 0; i < count / 7; i++) {
        const cells = [];
        for (let j = 0; j < 7 && cellIndex < count; j++) {
          cells.push(
            <td key={cellIndex} role="cell">
              {dayCells[cellIndex]}
            </td>
          );
          cellIndex++;
        }
        rows.push(
          <tr role="row" key={i}>
            {cells}
          </tr>
        );
      }
      return rows;
    }, [emptySlots, days]);

    const onPreviousMonth = useCallback(() => {
      let context;
      if (selectedMonth === 0) {
        context = new Date(selectedYear - 1, 11);
      } else {
        context = new Date(selectedYear, selectedMonth - 1);
      }
      setDateContext(context);
    }, [selectedMonth, selectedYear]);

    const onPreviousYear = useCallback(() => {
      const context = new Date(selectedYear - 1, selectedMonth);
      setDateContext(context);
    }, [selectedYear, selectedMonth]);

    const onNextMonth = useCallback(() => {
      let context;
      if (selectedMonth === 11) {
        context = new Date(selectedYear + 1, 0);
      } else {
        context = new Date(selectedYear, selectedMonth + 1);
      }
      setDateContext(context);
    }, [selectedMonth, selectedYear]);

    const onNextYear = useCallback(() => {
      const context = new Date(selectedYear + 1, selectedMonth);
      setDateContext(context);
    }, [selectedYear, selectedMonth]);

    if (!areParametersValid) {
      return <div>{`invalid props`}</div>;
    }

    return (
      <div className={contentClassNames} style={style}>
        <header className={styles.header}>
          <div className={styles.navigation}>
            <IconButton
              type="button"
              iconName={'chevron-left'}
              size="large"
              disabled={previousMonthDisabled}
              onClick={onPreviousMonth}
              aria-label="previous month"
              className={styles.back}
            />
            <p className={styles['current-value']}>{months[selectedMonth]}</p>
            <IconButton
              type="button"
              iconName={'chevron-right'}
              size="large"
              disabled={nextMonthDisabled}
              onClick={onNextMonth}
              aria-label="next month"
              className={styles.forward}
            />
          </div>
          <div className={styles.navigation}>
            <IconButton
              type="button"
              iconName={'chevron-left'}
              size="large"
              disabled={previousYearDisabled}
              onClick={onPreviousYear}
              aria-label="previous month"
              className={styles.back}
            />
            <p className={styles['current-value']}>{selectedYear}</p>
            <IconButton
              type="button"
              iconName={'chevron-right'}
              size="large"
              disabled={nextYearDisabled}
              onClick={onNextYear}
              aria-label="next month"
              className={styles.forward}
            />
          </div>
        </header>
        {/** Use event deligation to avoid multiple event listeners on each date */}
        <table className={styles.grid} onClick={onChange} role="grid">
          <thead role="rowgroup">{weekdaysHeader}</thead>
          <tbody role="rowgroup">{monthDays}</tbody>
        </table>
      </div>
    );
  }
);

DateSelector.displayName = 'DateSelector';
